import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { OfferSource } from '@cds';
import { FixtureFormMode } from '@cds/query-objects';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { ObservableGrid } from '@frontend/sports/grid/core/feature/observables';
import { DeviceService } from '@frontend/vanilla/core';
import { V1PickId, V2OptionMarketPickId } from 'packages/sports/common/betslip/core/picks/pick-id';
import { IPickTracking } from 'packages/sports/common/betslip/core/picks/pick-models';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';
import { take, toArray } from 'rxjs';

import { PopupManager } from '../../../popup/popup-manager.service';
import { TabBarItem } from '../../../tab-bar/tab-bar.models';

@Component({
    templateUrl: './form-coupon-header.component.html',
    selector: 'ms-form-coupon-header',
    styleUrls: ['./form-coupon-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FormCouponHeaderComponent implements OnChanges {
    @Input() mode: FixtureFormMode;
    @Input() grid: ObservableGrid;
    @Output() formSwitch = new EventEmitter<FixtureFormMode>();

    formPills: TabBarItem<FixtureFormMode>[];

    get active(): TabBarItem<FixtureFormMode> | undefined {
        return this.formPills?.filter((pill) => pill.active).pop();
    }

    constructor(
        private sitecore: Sitecore,
        private betslipIntegrationService: BetslipIntegrationService,
        private popupManager: PopupManager,
        private deviceService: DeviceService,
    ) {
        this.createPills(FixtureFormMode.Overall);
    }

    onFormSwitch(pill: TabBarItem<FixtureFormMode>): void {
        this.formPills = this.formPills.map((item) => ({
            ...item,
            active: item.id === pill.id,
        }));
        this.formSwitch.emit(pill.id);
    }

    addToBetslip(toAdd: number): void {
        const picksToAdd = [];
        const groups = this.grid.getState().groups;

        for (let i = 0; i < groups.length && picksToAdd.length < toAdd; i++) {
            for (let j = 0; j < groups[i].events.length && picksToAdd.length < toAdd; j++) {
                const event = groups[i].events[j];

                const mainGroup = event.optionGroups.find((optionGroup) => optionGroup.winning);
                if (!mainGroup) {
                    continue;
                }

                const bestOption = mainGroup?.options.find((option) => event.isBestPerformingOption(option, mainGroup));
                if (bestOption?.online) {
                    picksToAdd.push({
                        event,
                        market: mainGroup,
                        option: bestOption,
                        sourceofbet: { source: 'FormCoupon' },
                    });
                }
            }
        }

        this.addPicks(picksToAdd);
    }

    private addPicks(
        picksToAdd: {
            event: GridEvent;
            market: EventOptionGroup;
            option: EventOption;
            sourceofbet: IPickTracking;
        }[],
    ): void {
        if (!picksToAdd.length) {
            return;
        }

        let previousCount = 0;
        let currentCount = 0;
        this.betslipIntegrationService
            .betslipPicksCount$()
            .pipe(take(2), toArray())
            .subscribe(([prev, next]) => {
                previousCount = prev;
                currentCount = next;
            });

        const payload = {
            picks: picksToAdd.map((p) => ({
                pickId: this.createPickId(p),
                tracking: p.sourceofbet,
            })),
        };
        this.betslipIntegrationService.addMultiplePicks(payload);

        const somePicksAdded = currentCount > previousCount;

        if (somePicksAdded && this.deviceService.isMobile) {
            this.popupManager.open('betslip');
        }
    }

    private createPickId(pickToAdd: { event: GridEvent; market: EventOptionGroup; option: EventOption }): V1PickId | V2OptionMarketPickId {
        if (pickToAdd.event.offerSource === OfferSource.V1) {
            return new V1PickId(pickToAdd.option.originalEventId || pickToAdd.event.id, pickToAdd.option.optionGroupId, pickToAdd.option.id);
        } else {
            return new V2OptionMarketPickId(
                pickToAdd.event.hybridFixtureData != null
                    ? pickToAdd.event.hybridFixtureData.fixtureId
                    : pickToAdd.option.originalEventId || pickToAdd.event.id,
                pickToAdd.option.optionGroupId,
                pickToAdd.option.id,
                pickToAdd.option.priceId!,
            );
        }
    }

    private createPills(mode: FixtureFormMode): void {
        const createPill = (title: string, id: FixtureFormMode) => ({
            title,
            id,
            active: id === mode,
            classes: 'sports-pill',
        });
        this.formPills = [
            createPill(this.sitecore.formCoupon.BestOverallForm, FixtureFormMode.Overall),
            createPill(this.sitecore.formCoupon.BestHomeForm, FixtureFormMode.Home),
            createPill(this.sitecore.formCoupon.BestAwayForm, FixtureFormMode.Away),
        ];
    }

    ngOnChanges(changes: ISimpleChanges<FormCouponHeaderComponent>): void {
        if (changes.mode) {
            this.createPills(this.mode);
        }
    }
}
